import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TextField } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Modal from './Modal';

import { sendBadgeMessage, listBadges } from '../api';
import { setAlert, AlertSeverity } from '../redux/actions/alertActions';
import { T, FONT_MONO, INDICATOR_TEXT } from '../constants/tokens';

/** Characters a badge screen line holds. */
const LINE_LENGTH = 15;

/** How long the badge shows it. */
const DURATIONS = [1, 5, 15, 30, 60];

/** Only what the badge screen can show: printable ASCII, 15 to a line. */
const clean = (text) => text.replace(/[‘’]/g, '\'').replace(/[“”]/g, '"').replace(/[^\x20-\x7E]/g, '').slice(0, LINE_LENGTH);

/**
 * Write a message for one badge's screen (`badge`), or for every badge's
 * (no `badge`).
 *
 * The badge screen is shown as it will look, two lines of 15. Messages are
 * refused while an alert is in force, when the screen is carrying the alert,
 * so the dialog says so rather than letting the send fail.
 */
function BadgeMessageDialog({ open, onClose, badge = null, holderName = null, alertInForce, notify }) {
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [minutes, setMinutes] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [badgeCount, setBadgeCount] = useState(null);

  const allBadges = badge == null;

  useEffect(() => {
    if (!open) return;
    setLine1('');
    setLine2('');
    setMinutes(5);
    setError(null);
    setBadgeCount(null);
    // How many badges "every badge" is, so the button can say.
    if (allBadges) listBadges().then((badges) => setBadgeCount(badges.length)).catch(() => {});
  }, [open, allBadges]);

  const empty = !line1.trim() && !line2.trim();

  const send = () => {
    setBusy(true);
    setError(null);
    sendBadgeMessage(allBadges ? null : badge.getSerialNumber(), { line1: line1.trim(), line2: line2.trim(), minutes })
      .then((result) => {
        notify(result?.warning
          ? 'Message sent, but ' + result.warning + '.'
          : (allBadges ? 'Message sent to every badge.' : 'Message sent to the badge.'), result?.warning ? AlertSeverity.WARNING : AlertSeverity.SUCCESS);
        onClose();
      })
      .catch((reason) => setError(typeof reason === 'string' ? reason : 'The message could not be sent.'))
      .finally(() => setBusy(false));
  };

  const recipient = allBadges
    ? 'every badge' + (badgeCount != null ? ' (' + badgeCount + ')' : '')
    : (holderName ? holderName + '’s badge' : 'badge ' + badge.getSerialNumber());

  const line = (label, value, onChange) => (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(clean(e.target.value))}
      variant='outlined'
      size='small'
      fullWidth
      disabled={alertInForce}
      inputProps={{ maxLength: LINE_LENGTH, style: { fontFamily: FONT_MONO } }}
      helperText={value.length + ' / ' + LINE_LENGTH}
      FormHelperTextProps={{ style: { textAlign: 'right', marginRight: 0 } }}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow='Badge message'
      title={'Message ' + recipient}
      body={alertInForce
        ? 'Messages are not sent while an alert is in force: the badge screen is showing the alert.'
        : 'Shown on the badge screen for the time you choose. Two lines of up to 15 characters.'}
      actions={
        <>
          <Button variant='outlined' onClick={onClose}>Cancel</Button>
          <Button variant='contained' color='primary' onClick={send} disabled={busy || empty || alertInForce}>
            {busy ? 'Sending…' : (allBadges ? 'Send to every badge' : 'Send to badge')}
          </Button>
        </>
      }
    >
      {/* The badge screen as it will read. */}
      <div
        aria-hidden
        style={{
          fontFamily: FONT_MONO,
          fontSize: 16,
          lineHeight: '24px',
          whiteSpace: 'pre',
          background: '#1a1c16',
          color: '#e9f5d6',
          borderRadius: 6,
          padding: '12px 16px',
          marginBottom: 20,
          width: 'fit-content',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        {(line1 || ' ').padEnd(LINE_LENGTH, ' ')}
        {'\n'}
        {(line2 || ' ').padEnd(LINE_LENGTH, ' ')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {line('Line 1', line1, setLine1)}
        {line('Line 2', line2, setLine2)}
        <TextField
          select
          label='Show for'
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          variant='outlined'
          size='small'
          disabled={alertInForce}
          style={{ width: 180 }}
        >
          {DURATIONS.map((m) => <MenuItem key={m} value={m}>{m === 60 ? '1 hour' : m + (m === 1 ? ' minute' : ' minutes')}</MenuItem>)}
        </TextField>
      </div>

      {error && <div style={{ fontSize: 13, lineHeight: '18px', color: INDICATOR_TEXT.error, marginBottom: 16 }}>{error}</div>}
      {!allBadges && !badge.getIsConnected?.() &&
        <div style={{ fontSize: 13, lineHeight: '18px', color: T.body, marginBottom: 16 }}>
          This badge is not connected, so it may not receive the message.
        </div>}
    </Modal>
  );
}

BadgeMessageDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** The badge to message; every badge when absent. */
  badge: PropTypes.object,
  holderName: PropTypes.string,
  alertInForce: PropTypes.bool,
  notify: PropTypes.func
};

const mapStateToProps = (state) => ({
  // By name: a rehydrated status is a fresh AlertType, not the constant.
  alertInForce: !['Normal', 'All Clear', 'Unknown'].includes(state.status.schoolStatus?.getDashboardStatus?.() ?? 'Unknown')
});

const mapDispatchToProps = (dispatch) => ({
  notify: (message, severity) => setAlert(dispatch, message, severity, true)
});

export default connect(mapStateToProps, mapDispatchToProps)(BadgeMessageDialog);
